/**
 * Re-run a report generation from a stored (reportType, requestParams) pair -
 * the code path used by the Saved-configuration rail, the Recents list (see
 * components/SavedConfigsRail.jsx), and chat report handoff
 * (components/ReportHandoff.jsx). This mirrors the tail of
 * ReportsPage.handleGenerateReport (fetch -> envelope -> openReportWindow).
 * In-flight cancellation lives in this module
 * (createCancellableReportFetch) and is what the popup path uses.
 */
import { API_ENDPOINTS } from '../../../config/api';
import { runReportJob } from './reportJob';

function authToken() {
  return localStorage.getItem('access_token') || sessionStorage.getItem('access_token') || '';
}

/**
 * Group reports are the ones that outrun the ALB's 300s idle timeout: they
 * generate one full report PER MEMBER, so a 181-account back-dated group is
 * tens of minutes of replay, while a single-account report is seconds. So the
 * job-and-poll path is scoped exactly to them - everything else keeps the
 * simpler synchronous request, which is still the right shape when the answer
 * arrives well inside the timeout.
 */
export function isAsyncReportRequest(requestParams) {
  return Boolean(requestParams && requestParams.group_codename);
}

/**
 * A single report fetch that can be cancelled all the way to Athena.
 *
 * Aborting the fetch drops the client's wait; the /fetch-cancel POST reaches
 * the request's AthenaReportClient(s) by token and fires StopQueryExecution.
 * The async job path cancels the same way - its generation runs the identical
 * server code under the same token - so both routes share one cancel contract.
 *
 * State is PER CALL, never shared: several report windows can be generating at
 * once, so a shared holder let one window's close abort whichever generation
 * happened to be current - wrong report aborted, and a leak on top. Each caller
 * takes its own { fetchReport, cancel } pair.
 */
export function createCancellableReportFetch() {
  let controller = null;
  let activeToken = null;
  let cancelJob = null;

  const fetchReport = async (reportType, requestParams, { token, onProgress } = {}) => {
    if (isAsyncReportRequest(requestParams)) {
      try {
        return await runReportJob(reportType, requestParams, {
          token,
          onProgress,
          // Registered as soon as the job id exists so a close during the very
          // first poll interval still stops the server-side work. The job's own
          // cancel closes over the token, so `activeToken` (the sync path's
          // holder) stays untouched.
          onStart: (_jobId, cancelThisJob) => { cancelJob = cancelThisJob; },
        });
      } finally {
        cancelJob = null;
      }
    }
    const thisController = new AbortController();
    controller = thisController;
    activeToken = token || null;
    let response;
    try {
      response = await fetch(API_ENDPOINTS.reports.fetchData, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authToken()}` },
        body: JSON.stringify({ reportType, params: requestParams, endpoint: {}, cancel_token: token }),
        signal: thisController.signal,
      });
    } finally {
      // Only clear the shared holder if it still belongs to this call. If a second
      // overlapping fetchReport has already replaced it with its own controller,
      // clearing these fields out from under it would make cancel() silently do
      // nothing for that call - no abort, no cancel POST, and the Athena query leaks.
      if (controller === thisController) {
        controller = null;
        activeToken = null;
      }
    }
    const contentType = response.headers.get('content-type') || '';
    if (!contentType.includes('application/json')) {
      const text = await response.text();
      throw new Error(`Server returned non-JSON response (${response.status}): ${response.statusText}. ${text.substring(0, 120)}`);
    }
    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: `HTTP ${response.status}: ${response.statusText}` }));
      throw new Error(error.detail || `Failed to generate report: ${response.statusText}`);
    }
    const body = await response.json();
    if (body && body.cancelled) throw new Error('Report generation was cancelled');
    return body;
  };

  const cancel = () => {
    // Async job path: stop polling and tell the server to stop its Athena work.
    // It owns its own cancel POST, so this returns without touching `controller`
    // (which the job path never sets).
    if (cancelJob) {
      const stop = cancelJob;
      cancelJob = null;
      stop();
      return;
    }
    if (!controller) return;
    const token = activeToken;
    controller.abort();
    controller = null;
    activeToken = null;
    if (!token) return;
    fetch(API_ENDPOINTS.reports.fetchCancel, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authToken()}` },
      body: JSON.stringify({ cancel_token: token }),
      keepalive: true,
    }).catch(() => { /* fire-and-forget: StopQueryExecution is async server-side */ });
  };

  return { fetchReport, cancel };
}

/** A cancel token for one generation. */
export function newCancelToken() {
  return (typeof crypto !== 'undefined' && crypto.randomUUID)
    ? crypto.randomUUID()
    : `gen-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

/**
 * Merge a POST /api/reports/fetch-data envelope into the reportData shape
 * ReportWindow expects. Extracted from ReportsPage.handleGenerateReport so
 * both the popup Generate flow and the rail re-run produce identical shapes.
 */
export function buildReportEnvelope(result, requestParams) {
  const baseData = result.data || (result.reports ? {} : result);
  return {
    ...baseData,
    __mode: result.mode || 'single',
    __group: result.group || null,
    __reports: result.reports || null,
    __notice: result.notice || null,
    // Per-member failures on a degraded group. Absent on a clean run, so a
    // truthiness check is enough to decide whether to warn at all.
    __warnings: result.warnings || null,
    show_summary_information: requestParams.show_summary_information !== false,
    show_distribution_by_moody_rating: requestParams.show_distribution_by_moody_rating !== false,
    show_distribution_by_sp_rating: requestParams.show_distribution_by_sp_rating !== false,
    // Maturity section is opt-in (defaults OFF), so gate on strict true.
    show_distribution_by_maturity: requestParams.show_distribution_by_maturity === true,
    // AUM's accrued memo column is opt-in too (display-only; the payload
    // always carries the figures).
    include_accrued_interest: requestParams.include_accrued_interest === true,
  };
}

/**
 * Re-run a report generation from a stored (reportType, requestParams) pair.
 * Opens the native report popup synchronously (must be called from inside a
 * user-gesture handler - see openReportWindow), then fetches fresh data and
 * fills it in. Resolves to the { reportType, reportName, reportData,
 * requestParams } payload on success; rejects (e.g. Error('POPUP_BLOCKED'),
 * or a cancellation/fetch error, both of which also render in the popup's
 * error state) otherwise.
 */
export function runSavedReportGeneration({ reportType, requestParams, reportName, openReportWindow }) {
  return openReportWindow({
    reportName: reportName || 'Report',
    produce: async ({ onProgress } = {}) => {
      // The shared cancellable path: no token is passed, so this call carries no
      // cancel_token and cancel() is only reachable if a caller wires one up.
      // Keeping one fetch implementation means the rail, chat handoff, and the
      // report window all parse responses and surface errors identically - and
      // that a saved GROUP config re-runs through the async job path too.
      const { fetchReport } = createCancellableReportFetch();
      const result = await fetchReport(reportType, requestParams, { onProgress });
      const reportData = buildReportEnvelope(result, requestParams || {});
      return { reportType, reportName: reportName || 'Report', reportData, requestParams };
    },
  });
}
